import React from "react";
import PropTypes from "prop-types";
import { Tooltip } from "@mui/material";
import Fade from '@mui/material/Fade';
import { makeStyles } from '@mui/styles';
import { useMyContext } from '../../context/Context';

const useStyles = makeStyles({
  tooltip: {
    backgroundColor: "#F5F6F7",
    fontSize: 12,
    fontFamily: ["canada-type-gibson", "Roboto", "sans-serif"],
    fontWeight: 400,
    height: 18,
    color: "#004368",
    border: "1px solid #CCD5DF",
    borderRadius: 2,
    display: "flex",
    alignItems: "center",
    margin: 0,
    paddingTop: 6,
    paddingBottom: 4,
    position: "relative",
    left: -8,
    top: -8,
    boxShadow: "0px 8px 24px rgba(0, 39, 81, 0.15)",
    maxWidth: "none"
  },
  tooltipLeft: {
    left: 16,
    top: -15
  },
  chevronButton: {
    minWidth: 32,
    minHeight: 32,
    background: "none",
    border: "1px solid #CCD5DF",
    borderRadius: 3,
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  iconButton: {
    padding: 0,
    background: "none",
    border: "none",
    minHeight: 24,
    minWidth: 24,
    width: 24,
    marginRight: 8,
    marginLeft: 8,
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  iconDropdown: {
    cursor: "grabbing"
  }
});

const NavigationButton = props => {
  const classes = useStyles();
  const context = useMyContext();

  const isDark = context?.colorTheme === 'dark';
  const hoverColor = isDark ? "#2A2A2A" : "#FFFFFF";
  const borderColor = isDark ? "#2A2A2A" : "#CCD5DF";

  let tipPlacement,
    tipClasses,
    buttonClasses = "";

  // Conditional Tooltip styles
  if (props.tipPosition === "left") {
    tipPlacement = "left-start";
    tipClasses = `${classes.tooltip} ${classes.tooltipLeft}`;
  }

  if (props.tipPosition === "right") {
    tipPlacement = "right-start";
    tipClasses = classes.tooltip;
  }

  // Conditional Button styles
  let customStyle = {};

  if (props.type === "icon") {
    buttonClasses = classes.iconButton;
    customStyle = { border: 'none' };
    if (props.openDropdown) {
      buttonClasses = `${classes.iconButton} ${classes.iconDropdown}`;
    }
  }

  if (props.type === "nav") {
    buttonClasses = classes.chevronButton;
    customStyle = { border: `1px solid ${borderColor}` };
  }

  return (
    <Tooltip
      title={!props.disabled ? props.title : ""}
      id={props.id + "-tootlip"}
      placement={tipPlacement}
      classes={{ tooltip: tipClasses }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 600 }}
    >
      <span style={{ display: 'inline-block' }}>
        <button
          className={buttonClasses}
          id={props.id}
          aria-describedby={props.id + "-tooltip"}
          onClick={props.clickHandler}
          aria-expanded={props.ariaExpanded}
          aria-controls={props.ariaControls}
          disabled={props.disabled}
          style={customStyle}
          onMouseEnter={e => {
            e.currentTarget.style.setProperty('background', hoverColor, 'important');
            // Also set background on any child elements
            const children = e.currentTarget.querySelectorAll('*');
            children.forEach(child => {
              child.style.setProperty('background', 'transparent', 'important');
            });
          }}
          onMouseLeave={e => {
            e.currentTarget.style.setProperty('background', 'none', 'important');
            // Reset child elements
            const children = e.currentTarget.querySelectorAll('*');
            children.forEach(child => {
              child.style.removeProperty('background');
            });
          }}
        >
          {props.children}
        </button>
      </span>
    </Tooltip>
  );
};

NavigationButton.propTypes = {
  tipPosition: PropTypes.string,
  title: PropTypes.string,
  id: PropTypes.string.isRequired,
  clickHandler: PropTypes.func,
  ariaExpanded: PropTypes.bool,
  ariaControls: PropTypes.string,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  type: PropTypes.string,
  openDropdown: PropTypes.bool
};

const CustomTooltip = props => {
  const classes = useStyles();

  let tipPlacement, tipClasses = "";

  if (props.tipPosition === "left") {
    tipPlacement = "left-start";
    tipClasses = `${classes.tooltip} ${classes.tooltipLeft}`;
  }

  if (props.tipPosition === "right") {
    tipPlacement = "right-start";
    tipClasses = classes.tooltip;
  }

  return (
    <Tooltip
      title={props.title}
      id={props.id + "-tootlip"}
      placement={tipPlacement}
      classes={{ tooltip: tipClasses }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 600 }}
    >
        {props.children}
    </Tooltip>
  );

};

CustomTooltip.propTypes = {
  tipPosition: PropTypes.string,
  title: PropTypes.string,
  id: PropTypes.string,
  children: PropTypes.node
};

export { NavigationButton, CustomTooltip };
